import { getListingStageByDefaultName } from '@/features/listing-stage/helpers/get-listing-stage-by-default-name';
import { useListingStages } from '@/features/listing-stage/use-listing-stages.query';

import { isDealStage, ListingStageName } from '../../../listing-stage/types';
import { projectIdState } from '../../project.state';
import { useProjectListings } from '../use-project-listings.query';

import { type TabStage, useStageTabsConfig } from './use-stage-tabs-config';
import { useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';

export const useListingStagesToSwitchTabs = () => {
  const projectId = useAtomValue(projectIdState);
  const { projectListings } = useProjectListings({ projectId });
  const { listingStages } = useListingStages();
  const [, setSearchParams] = useSearchParams();
  const stageTabs = useStageTabsConfig();

  const getDropToDestination = (currentTab: TabStage) => {
    if (
      currentTab.name === ListingStageName.notRelevant ||
      currentTab.name === ListingStageName.deal
    ) {
      return;
    }

    return stageTabs?.find(
      stage => stage.sortOrder === currentTab.sortOrder + 1,
    );
  };

  const getFirstStageWithListings = (): TabStage | undefined => {
    const tab = stageTabs?.find(tab => isAnyCompanyAtStage(tab));

    return tab;
  };

  const isAnyCompanyAtStage = (tabStage: TabStage) => {
    if (tabStage.name === ListingStageName.notRelevant) {
      return projectListings.some(listing => listing.notRelevant);
    }

    if (tabStage.name === ListingStageName.deal) {
      return projectListings.some(
        listing => isDealStage(listing.status.name) && !listing.notRelevant,
      );
    }
    return projectListings.some(
      listing => listing.status.id === tabStage.id && !listing.notRelevant,
    );
  };

  const setActiveTabByStageName = (stageName: string) => {
    const stage = getListingStageByDefaultName(stageName, listingStages);

    if (!stage) {
      return;
    }

    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set(
          'stage',
          isDealStage(stage.name) ? ListingStageName.deal : stage.id,
        );
        return next;
      },
      { replace: true },
    );
  };

  const setActiveFirstTabWithListings = () => {
    const firstStageWithListings = getFirstStageWithListings();
    if (!firstStageWithListings) {
      return;
    }
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('stage', firstStageWithListings.id);
        return next;
      },
      { replace: true },
    );
  };

  return {
    getDropToDestination,
    isAnyCompanyAtStage,
    setActiveTabByStageName,
    setActiveFirstTabWithListings,
  };
};
